<template>
  <div class="max-md:-mx-4.5">
    <div class="flex flex-col print:gap-4 lg:gap-8">
      <div class="w-full flex-none print:hidden">
        <ImageGallery :images="product.images">
          <template #badges>
            <BadgesWrapper>
              <PurchasedBeforeBadge v-if="product.isPurchased" />

              <DiscountBadge static :price="product.price" />
            </BadgesWrapper>
          </template>
        </ImageGallery>
      </div>

      <div class="product-info__print">
        <VcImage :src="product.imgSrc" class="product-info__print-img" />
      </div>

      <div class="product-info__content">
        <div v-if="model?.blocks?.length" class="product-info__blocks">
          <template v-for="(block, index) in model.blocks">
            <component
              :is="block.type"
              v-if="block.type !== 'product-variations'"
              :key="block.id || index"
              :model="block"
              :product="product"
            />
          </template>
        </div>

        <div v-if="product.isConfigurable" class="product-info__config">
          <VcButton
            color="secondary"
            variant="outline"
            prepend-icon="cube-transparent"
            @click="handleCreateConfiguration"
          >
            {{ $t("shared.catalog.product_details.create_configuration_button") }}
          </VcButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ImageGallery, DiscountBadge, ProductVideos } from "@/shared/catalog";
import type { IPageContent } from "../types";
import type { Product } from "@/core/api/graphql/types";
import BadgesWrapper from "@/shared/catalog/components/badges-wrapper.vue";
import PurchasedBeforeBadge from "@/shared/catalog/components/purchased-before-badge.vue";

interface IProps {
  product: Product;
  model: IPageContent;
}

defineProps<IProps>();

function handleCreateConfiguration() {
  const productConfigurationElement = document.getElementById("product-configuration-anchor");
  if (productConfigurationElement) {
    productConfigurationElement.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
  }
}
</script>

<style lang="scss">
.product-info {
  &__container {
    @apply flex flex-col lg:flex-row lg:gap-8 print:flex-row print:gap-4;
  }

  &__side {
    @apply flex-none lg:w-80 xl:w-[27.5rem] 2xl:w-[30rem] print:hidden;
  }

  &__videos {
    @apply mt-8 lg:mt-3;
  }

  &__print {
    @apply hidden aspect-square w-40 flex-none print:block;
  }

  &__print-img {
    @apply w-full rounded border;
  }

  &__content {
    @apply mt-5 lg:mt-0 lg:grow print:mt-5 print:grow;
  }

  &__blocks {
    @apply flex flex-col gap-6 mb-6;
  }
}
</style>
